import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  MessageCircle,
  Menu,
  X,
  Zap,
  ShieldCheck,
  Handshake,
  Sparkles,
  Users,
  Trophy,
  Layers,
  MapPin,
  Compass,
  Rocket,
  UserCheck,
  Globe,
  Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, RevealStagger } from "@/components/motion/Reveal";
import { PartnersSection } from "@/components/Partners";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";
import filippoAsset from "@/assets/founder-filippo.webp.asset.json";

const WA_URL =
  "https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AHo%20letto%20la%20pagina%20Chi%20Siamo%20e%20vorrei%20parlarti%20del%20mio%20progetto.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20tuo%20contatto.";

export const Route = createFileRoute("/chi-siamo")({
  head: () => ({
    meta: [
      { title: "Chi Siamo — FlashWeb Agency | Precisione svizzera dal 2023" },
      {
        name: "description",
        content:
          "FVT FuturaVision Technology GmbH, il team dietro FlashWeb Agency. Scopri il metodo, i numeri e chi segue personalmente ogni sito, dal briefing alla messa online.",
      },
      { property: "og:title", content: "Chi Siamo — FlashWeb Agency" },
      {
        property: "og:description",
        content:
          "Il metodo, i numeri e le persone dietro FlashWeb Agency. Precisione svizzera dal 2023.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/chi-siamo" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/chi-siamo" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "Chi siamo", item: "https://www.flash-web.ch/chi-siamo" },
          ],
        }),
      },
    ],
  }),
  component: ChiSiamoPage,
});

// ─── Local navbar (matches home) ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

// ─── Local footer (matches home) ───
function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} — Tutti i diritti riservati.
            </p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <span className="font-semibold text-foreground">Bareerstrasse 75</span><br />
                6300 Zug, Svizzera
              </p>
              <p>
                <span className="font-semibold text-foreground">Via Ai Prati 2</span><br />
                Bedano, Svizzera
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a>
              </p>
              <p>
                <a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a>
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">
              N° registro commercio:<br />
              <span className="font-semibold text-foreground">CH-170.4.022.832-9</span>
            </p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════ PAGE ═══════════════
function ChiSiamoPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* 1. HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center sm:py-32">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              La nostra storia
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl md:text-7xl">
              Non un'agenzia. <span className="text-gradient">Un metodo.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl">
              FlashWeb nasce dalla frustrazione di vedere PMI svizzere e italiane aspettare
              settimane e pagare preventivi opachi per un semplice sito web.
              <br className="hidden sm:block" />
              Abbiamo costruito un processo che consegna in 48 ore, con prezzo fisso e
              zero sorprese.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <a href="#metodo">
                  Scopri il metodo <ArrowRight />
                </a>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5" /> Parla con Filippo
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2. NUMERI */}
      <section className="relative mx-auto w-full max-w-6xl px-6 py-20">
        <Reveal>
          <div className="mb-12 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              I numeri
            </div>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Quello che <span className="text-gradient">abbiamo consegnato</span> finora.
            </h2>
          </div>
        </Reveal>
        <RevealStagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" stagger={0.08}>
          {[
            { n: "60+", l: "Siti online", Icon: Rocket },
            { n: "4.9/5", l: "Valutazione media", Icon: Sparkles },
            { n: "48h", l: "Tempo di consegna", Icon: Zap },
            { n: "14", l: "Settori serviti", Icon: Layers },
            { n: "100%", l: "Consegne garantite o rimborso", Icon: ShieldCheck },
          ].map(({ n, l, Icon }) => (
            <div
              key={l}
              className="rounded-2xl border border-border bg-card/60 p-6 backdrop-blur transition-colors hover:border-primary/50"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-5 font-display text-4xl font-bold tracking-tight text-gradient sm:text-5xl">
                {n}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">{l}</div>
            </div>
          ))}
        </RevealStagger>
      </section>

      {/* 3. METODO */}
      <section id="metodo" className="relative border-y border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0 opacity-50" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto w-full max-w-4xl px-6 py-20 sm:py-28">
          <Reveal>
            <div className="mb-14 max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                Il metodo
              </div>
              <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Come lavoriamo, <span className="text-gradient">davvero.</span>
              </h2>
              <p className="mt-4 text-muted-foreground">
                Quattro tappe. Nessun mistero, nessun preventivo infinito.
              </p>
            </div>
          </Reveal>

          <ol className="relative border-l border-border/60 pl-8 sm:pl-10">
            {[
              {
                Icon: Compass,
                title: "Briefing guidato",
                time: "15 minuti",
                desc: "Un questionario intelligente raccoglie tutto quello che serve. Niente call da due ore, niente slide.",
              },
              {
                Icon: Layers,
                title: "Costruzione in parallelo",
                time: "24–36 ore",
                desc: "Design, contenuti e SEO on-page vengono lavorati nello stesso sprint, non uno dopo l'altro.",
              },
              {
                Icon: UserCheck,
                title: "Revisione con il founder",
                time: "in giornata",
                desc: "Parli direttamente con Filippo, non con un account manager. Modifiche approvate in tempo reale.",
              },
              {
                Icon: Globe,
                title: "Messa online + 30 giorni",
                time: "assistenza inclusa",
                desc: "Pubblicazione, dominio, hosting e un mese di modifiche incluse per rifinire ogni dettaglio.",
              },
            ].map(({ Icon, title, time, desc }, i) => (
              <li key={title} className="relative mb-10 last:mb-0">
                <Reveal delay={i * 0.08}>
                  <span className="absolute -left-[46px] flex h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-background text-primary shadow-[0_0_20px_-4px_var(--primary)] sm:-left-[54px]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="rounded-2xl border border-border bg-background/70 p-6 backdrop-blur transition-colors hover:border-primary/50">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <h3 className="font-display text-xl font-semibold tracking-tight">
                        {i + 1}. {title}
                      </h3>
                      <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                        {time}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground sm:text-base">{desc}</p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 4. FILOSOFIA */}
      <section className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-28">
        <Reveal>
          <div className="mb-12 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              La filosofia
            </div>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Tre principi <span className="text-gradient">non negoziabili.</span>
            </h2>
          </div>
        </Reveal>

        <RevealStagger className="grid gap-6 md:grid-cols-3" stagger={0.12}>
          {[
            {
              Icon: ShieldCheck,
              t: "Tariffa fissa, sempre",
              d: "Il prezzo è quello che vedi. Nessuna riga nascosta in fattura, nessun extra a fine progetto.",
            },
            {
              Icon: Handshake,
              t: "Un solo punto di contatto",
              d: "Parli sempre con chi costruisce il tuo sito. Nessun commerciale, nessun filtro, nessun account manager.",
            },
            {
              Icon: Trophy,
              t: "Consegna o non paghi",
              d: "Se non rispettiamo le 48 ore concordate nel briefing, il progetto è gratuito. Semplice.",
            },
          ].map(({ Icon, t, d }) => (
            <div
              key={t}
              className="group relative rounded-2xl border border-border bg-card/60 p-7 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-[0_20px_60px_-20px_var(--primary)]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold">{t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </RevealStagger>
      </section>

      {/* 5. FOUNDER */}
      <section className="relative border-y border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-28">
          <Reveal>
            <div className="mb-12 max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                Il founder
              </div>
              <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Dietro FlashWeb c'è <span className="text-gradient">una persona.</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid gap-10 lg:grid-cols-[380px_1fr] lg:items-start">
            <Reveal>
              <div className="group relative overflow-hidden rounded-3xl border border-border bg-card">
                <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "var(--gradient-hero)" }} />
                <div className="relative flex flex-col items-center gap-6 p-8 text-center">
                  <div className="relative">
                    <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-primary to-accent opacity-70 blur-md transition-opacity group-hover:opacity-100" />
                    <img
                      src={filippoAsset.url}
                      alt="Filippo Strazzanti, founder di FlashWeb"
                      width={400}
                      height={400}
                      className="relative h-44 w-44 rounded-full object-cover ring-4 ring-background"
                    />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold">Filippo Strazzanti</h3>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-primary">
                      Founder & Growth Lead
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2">
                    {["Contatto diretto", "Nessun intermediario", "Supervisione personale"].map((b) => (
                      <span
                        key={b}
                        className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> Zug · Bedano (TI) · Svizzera
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="space-y-5 text-muted-foreground">
                <p className="text-lg leading-relaxed text-foreground/90">
                  Mi chiamo Filippo. Ho fondato <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>{" "}
                  a Zug perché ero stanco di vedere piccoli imprenditori bravi finire online con
                  siti brutti, lenti o mai finiti.
                </p>
                <p>
                  Prima di FlashWeb ho lavorato per anni tra sviluppo prodotto e crescita
                  digitale, aiutando aziende ticinesi, lombarde e svizzero-tedesche a
                  strutturare la loro presenza web. La stessa storia si ripeteva ogni volta:
                  <span className="font-semibold text-foreground"> preventivi da 6.000 franchi, tre mesi di attesa, e alla fine un sito che
                  nessuno vuole più toccare.</span>
                </p>
                <p>
                  Ho scelto Zug per la disciplina svizzera nella gestione fiscale e legale, e il
                  Ticino per restare vicino ai clienti che parlano la mia lingua. Da qui
                  costruisco FlashWeb come lo avrei voluto da cliente: un solo prezzo, una sola
                  persona con cui parlare, 48 ore di tempo per andare live.
                </p>
                <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-6">
                  <div className="flex items-start gap-4">
                    <Quote className="h-8 w-8 shrink-0 text-primary/70" />
                    <blockquote className="font-display text-xl font-semibold leading-snug tracking-tight sm:text-2xl">
                      "Ho creato FlashWeb perché ottenere un sito professionale debba essere
                      semplice: prezzo chiaro, tempi certi e nessuna sorpresa."
                    </blockquote>
                  </div>
                  <p className="mt-4 pl-12 text-sm text-muted-foreground">
                    — <span className="font-semibold text-foreground">Filippo Strazzanti</span>, Founder
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 6. STACK — reuse Partners */}
      <PartnersSection />

      {/* 7. CTA FINALE */}
      <section className="relative mx-auto w-full max-w-5xl px-6 py-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-[36px] border border-primary/25 bg-gradient-to-br from-neutral-950 via-neutral-900 to-black p-12 sm:p-20">
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -left-1/4 top-1/4 h-96 w-96 rounded-full bg-primary/30 blur-3xl" />
              <div className="absolute -right-1/4 bottom-1/4 h-96 w-96 rounded-full bg-fuchsia-500/25 blur-3xl" />
            </div>
            <div className="relative mx-auto max-w-2xl text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                <Users className="h-3.5 w-3.5" /> Rispondiamo in giornata
              </div>
              <h3 className="mt-6 font-display text-4xl font-bold leading-tight sm:text-5xl">
                Pronto a <span className="text-gradient">conoscerci di persona?</span>
              </h3>
              <p className="mt-5 text-lg text-neutral-300">
                Scrivici su WhatsApp: nessun bot, nessun form infinito. Ti risponde Filippo.
              </p>
              <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary to-fuchsia-500 px-8 py-5 text-base font-bold text-primary-foreground shadow-[0_20px_60px_-10px_var(--primary)] transition-shadow hover:shadow-[0_25px_80px_-5px_var(--primary)] sm:w-auto"
                >
                  <MessageCircle className="h-5 w-5" /> Scrivici su WhatsApp
                </a>
                <Link
                  to="/"
                  hash="price"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-neutral-700 bg-neutral-900/60 px-8 py-5 text-base font-semibold text-neutral-100 transition-colors hover:border-neutral-500 hover:bg-neutral-800 sm:w-auto"
                >
                  Vedi i prezzi <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
